import { RefreshCw } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import type { BusinessDetailsView, LivePosControlLicenseInstallation } from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import {
  Badge,
  Button,
  EmptyState,
  FormField,
  Input,
  Modal,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { applyRemoteBusinessLicense } from '@/data';
import { errorMessage } from '@/lib/api';
import { formatDate, formatDateTime, formatRelative } from '@/lib/format';

const newKey = () => crypto.randomUUID();

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function oneYearFrom(base: string | null | undefined): string {
  const start = base ? new Date(base) : new Date();
  const today = new Date();
  const d = Number.isNaN(start.getTime()) || start < today ? today : start;
  const out = new Date(d);
  out.setFullYear(out.getFullYear() + 1);
  return isoDay(out);
}

export function PharmacyLicenseManager({
  businessId,
  businessName,
  details,
  onRefresh,
}: {
  businessId: string;
  businessName: string;
  details: BusinessDetailsView;
  onRefresh: () => void | Promise<void>;
}) {
  const canManage = useCan()('license.manage');
  const productionAvailable = details.overview.productionAvailable === true;
  const installations = useMemo(
    () => details.licensing.posInstallations ?? [],
    [details.licensing.posInstallations],
  );

  const [target, setTarget] = useState<LivePosControlLicenseInstallation | null>(null);
  const [expiryDate, setExpiryDate] = useState(oneYearFrom(null));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ commandStatus: string; requestedExpiryDate: string } | null>(null);
  const keyRef = useRef(newKey());

  const openRenew = (row: LivePosControlLicenseInstallation) => {
    keyRef.current = newKey();
    setError(null);
    setLastResult(null);
    setExpiryDate(oneYearFrom(row.installedExpiryDate));
    setTarget(row);
  };

  const closeModal = () => {
    setTarget(null);
    setError(null);
    setSubmitting(false);
  };

  const submitApply = async () => {
    if (!target || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await applyRemoteBusinessLicense(businessId, {
        productionBranchId: target.productionBranchId,
        controlDeviceId: target.controlDeviceId,
        licenseDeviceId: target.licenseDeviceId ?? undefined,
        expiryDate,
        customerName: businessName,
        idempotencyKey: keyRef.current,
      });
      setLastResult({ commandStatus: result.commandStatus, requestedExpiryDate: result.requestedExpiryDate });
      await onRefresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const actionsDisabled = !productionAvailable || !canManage;

  return (
    <section aria-label="Installed POS licenses" className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-ink">Installed POS licenses</h3>
        <p className="mt-0.5 text-xs text-ink-secondary">
          Live license state reported by POS control at linked cloud branches. Renew pushes an APPLY_LICENSE command to the
          POS (no manual activation code).
        </p>
      </div>

      {!productionAvailable && (
        <Badge tone="warning">Production data temporarily unavailable — remote renew is disabled</Badge>
      )}

      {installations.length === 0 ? (
        <EmptyState
          compact
          title="No POS control installations"
          description="Link cloud branches and ensure POS devices check in via pos-control to see installed licenses here."
        />
      ) : (
        <TableContainer label="POS installations">
          <Table>
            <THead>
              <TR>
                <TH>Branch</TH>
                <TH>Control device</TH>
                <TH>License device</TH>
                <TH>Installed expiry</TH>
                <TH>Issued</TH>
                <TH>Verify</TH>
                <TH>Last reported</TH>
                <TH>Pending apply</TH>
                {canManage && <TH align="right">Actions</TH>}
              </TR>
            </THead>
            <TBody>
              {installations.map((row) => (
                <TR key={`${row.productionBranchId}:${row.controlDeviceId}`}>
                  <TD>{row.branchName}</TD>
                  <TD>
                    <span className="font-mono text-xs text-ink-secondary">{row.controlDeviceId}</span>
                  </TD>
                  <TD>
                    <span className="font-mono text-xs">{row.licenseDeviceId ?? '—'}</span>
                  </TD>
                  <TD>{row.installedExpiryDate ? formatDate(row.installedExpiryDate) : '—'}</TD>
                  <TD>{row.licenseIssuedAt ? formatDate(row.licenseIssuedAt) : '—'}</TD>
                  <TD>
                    {row.licenseVerifyOk === null ? (
                      '—'
                    ) : (
                      <Badge tone={row.licenseVerifyOk ? 'success' : 'warning'} dot>
                        {row.licenseVerifyOk ? 'OK' : 'Failed'}
                      </Badge>
                    )}
                  </TD>
                  <TD>{row.licenseLastReportedAt ? formatRelative(row.licenseLastReportedAt) : '—'}</TD>
                  <TD>
                    {row.pendingApplyCommand ? (
                      <span className="text-xs">
                        {row.pendingApplyCommand.status}
                        {row.pendingApplyCommand.requestedExpiryDate
                          ? ` → ${formatDate(row.pendingApplyCommand.requestedExpiryDate)}`
                          : ''}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TD>
                  {canManage && (
                    <TD align="right">
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<RefreshCw size={14} aria-hidden="true" />}
                        disabled={actionsDisabled}
                        title={actionsDisabled ? 'Unavailable' : undefined}
                        onClick={() => openRenew(row)}
                      >
                        Renew
                      </Button>
                    </TD>
                  )}
                </TR>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      )}

      {target && (
        <Modal
          open
          onClose={closeModal}
          dismissible={!submitting}
          title="Renew installed license"
          description={`${target.branchName} · ${target.controlDeviceId}`}
          icon={<RefreshCw size={18} aria-hidden="true" />}
          size="md"
          footer={
            lastResult ? (
              <Button variant="primary" onClick={closeModal}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={closeModal} disabled={submitting} className="mr-auto">
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => void submitApply()} loading={submitting} disabled={submitting || !expiryDate}>
                  Save
                </Button>
              </>
            )
          }
        >
          <div className="space-y-4">
            {error && (
              <p role="alert" className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                {error}
              </p>
            )}
            {lastResult ? (
              <p className="rounded-md border border-success/30 bg-success-soft px-3 py-2 text-sm text-success">
                Remote apply queued — status <strong>{lastResult.commandStatus}</strong> for expiry{' '}
                {formatDate(lastResult.requestedExpiryDate)}. The POS will apply on next check-in.
              </p>
            ) : (
              <>
                <p className="text-xs text-ink-muted">
                  Current installed expiry:{' '}
                  {target.installedExpiryDate ? formatDate(target.installedExpiryDate) : 'unknown'}
                  {target.licenseLastReportedAt ? ` · last reported ${formatDateTime(target.licenseLastReportedAt)}` : ''}
                </p>
                <FormField label="New expiration" htmlFor="pl-renew-expiry" required>
                  <Input
                    id="pl-renew-expiry"
                    type="date"
                    value={expiryDate}
                    onChange={(e) => {
                      keyRef.current = newKey();
                      setExpiryDate(e.target.value);
                    }}
                    disabled={submitting}
                  />
                </FormField>
              </>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}
